import React, { useState } from 'react';
import { Box, Button, Stack, SxProps, Tooltip, Typography } from '@mui/material';

import { MaterialPropertyValuePair, PropertySelectField, PropertyTextInput } from '@paragon/client-tools/';
import { ConnectingHardware, Hanger, StandardConnectingHardwareModel } from '@paragon/contract-library';
import { PropertiesPanel } from '@paragon/layout-models';
import { hasPricingPermission as hasPricingPermissionSelector } from '@paragon/login-tools';
import { DistanceField } from '@paragon/material';
import { getConnectingHardwareName } from '@paragon/material-lists';
import { assertExhaustiveSwitch, humanCase, naturalSortByProperty, notNullish } from '@paragon/utilities';

import { changeHangers } from '../../../../store/actions/paragonProcessActions';
import { selectItem } from '../../../../store/actions/selectActions';
import { getActiveComponentDesign } from '../../../../store/selectors/getActiveComponentDesignSelector';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { PropertiesPanelMenuButton } from './PropertiesPanelMenuButton';
import { PropertiesPanelSubtitle } from './PropertiesPanelSubtitle';

export function HangerProperties(props: { value: Hanger }) {
  const hanger = props.value;
  const standardModel = hanger.connector.standardOrCustom === 'Standard' ? hanger.connector.model : null;

  const dispatch = useAppDispatch();

  const componentDesigns = useAppSelector((state) => state.componentDesigns);
  const jackGirderConnections = useAppSelector((state) => state.jackGirderConnections.data);
  const activeComponentDesign = useAppSelector(getActiveComponentDesign);
  const hasPricingPermission = useAppSelector(hasPricingPermissionSelector);
  const hangerPrices = useAppSelector((state) => state.inventoryPrices.hangerPrices);
  const hangerPrice = standardModel && hangerPrices[standardModel];

  if (!activeComponentDesign) return null;

  const carriedComponentDesignsIncludingUnfound = Object.values(jackGirderConnections)
    .filter(({ hangerGuid }) => hangerGuid === hanger.guid)
    .map(({ jackGuid }) => Object.values(componentDesigns.data).find(({ guid }) => guid === jackGuid));

  const unfoundCarriedComponentDesigns = carriedComponentDesignsIncludingUnfound.filter((item) => item == null).length;
  const carriedComponentDesigns = carriedComponentDesignsIncludingUnfound.filter(notNullish);

  return (
    <PropertiesPanel
      // Avoids stale state in the ConnectingHardwareSelect below when switching from a just-set custom hanger to a
      // standard hanger
      key={props.value.guid}
      title="Connection Properties"
      titleButton={<PropertiesPanelMenuButton type="hanger" guid={props.value.guid} />}
    >
      <ConnectingHardwareSelect
        value={hanger.connector}
        onChange={async (newConnector) =>
          await dispatch(changeHangers(activeComponentDesign, hanger.guid, newConnector))
        }
      />
      <MaterialPropertyValuePair property="Girder Face" value={humanCase(hanger.locationType)} />

      {hasPricingPermission && (
        <MaterialPropertyValuePair
          property="Hanger Price"
          value={hangerPrice == null ? 'Not available' : `$${hangerPrice.toFixed(2)}`}
        />
      )}

      <PropertiesPanelSubtitle>Location</PropertiesPanelSubtitle>
      <DistanceField disabled label="X" value={hanger.locationOnGirder.x} />
      <DistanceField disabled label="Y" value={hanger.locationOnGirder.y} />

      {carriedComponentDesignsIncludingUnfound.length === 0 ? null : (
        <PropertiesPanelSubtitle>Carried Components</PropertiesPanelSubtitle>
      )}
      <Stack spacing={1}>
        {naturalSortByProperty(carriedComponentDesigns, ({ component }) => component.name).map(
          (carriedComponentDesign) => (
            <Tooltip key={carriedComponentDesign.guid} title={`View ${carriedComponentDesign.component.name}`}>
              <Button
                variant="outlined"
                onClick={() => dispatch(selectItem(carriedComponentDesign, 'componentDesign'))}
                key={`carried-component-design-${carriedComponentDesign.guid}`}
              >
                {carriedComponentDesign.component.name}
              </Button>
            </Tooltip>
          ),
        )}
        {unfoundCarriedComponentDesigns === 0 ? null : (
          <Typography>{unfoundCarriedComponentDesigns} Not Shown</Typography>
        )}
      </Stack>
    </PropertiesPanel>
  );
}

const ConnectingHardwareSelect = (props: {
  value: ConnectingHardware;
  onChange: (value: ConnectingHardware) => void | Promise<void>;
}) => {
  const cancelButtonId = 'connectingHardwareSelectCancelButton';
  const buttonStyle: SxProps = { m: 1, mt: 2, mr: 0, whiteSpace: 'nowrap' };
  const [customOverrideText, setCustomOverrideText] = useState<string | null>(null);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'row' }}>
      {customOverrideText != null || props.value.standardOrCustom === 'Custom' ? (
        <>
          <PropertyTextInput
            property="Hardware"
            fullWidth
            sx={{ marginBottom: '8px !important' }}
            value={customOverrideText ?? (props.value.standardOrCustom === 'Custom' ? props.value.name : '')}
            submit={async (name) => {
              if (name !== '' && (props.value.standardOrCustom === 'Standard' || props.value.name !== name)) {
                const maybePromise = props.onChange({ standardOrCustom: 'Custom', name });
                if (maybePromise) await maybePromise;
                setCustomOverrideText(null);
              }
            }}
            onBlur={(event) => {
              if (event.relatedTarget?.id === cancelButtonId) event.stopPropagation();
            }}
          />
          {customOverrideText != null && (
            <Button sx={buttonStyle} id={cancelButtonId} variant="outlined" onClick={() => setCustomOverrideText(null)}>
              Cancel
            </Button>
          )}
        </>
      ) : (
        <>
          <PropertySelectField<StandardConnectingHardwareModel>
            property="Hardware"
            value={props.value.model}
            getName={getConnectingHardwareName}
            options={getHardwareExchangeOptions(props.value.model)}
            submit={(model) => props.onChange({ standardOrCustom: 'Standard', model })}
          />
          <Button
            sx={buttonStyle}
            variant="outlined"
            onClick={() => {
              if (props.value.standardOrCustom === 'Custom') throw new Error('Expected standard hanger hardware');
              return setCustomOverrideText(getConnectingHardwareName(props.value.model));
            }}
          >
            Write-in
          </Button>
        </>
      )}
    </Box>
  );
};

/**
 * Currently, we only allow exchanging hardware for another piece that we are certain will respect the same basic
 * constraints such as plies, width, and angle.
 */
function getHardwareExchangeOptions(
  currentHardware: StandardConnectingHardwareModel,
): readonly StandardConnectingHardwareModel[] {
  switch (currentHardware) {
    case 'HUS26':
    case 'HUS28':
    case 'LUS24':
    case 'LUS26':
    case 'LUS28':
    case 'MUS26':
    case 'MUS28':
      return ['HUS26', 'HUS28', 'LUS24', 'LUS26', 'LUS28', 'MUS26', 'MUS28'];

    case 'TJC37':
      return ['TJC37'];

    case 'HHUS26_2':
    case 'HHUS28_2':
    case 'LUS24_2':
      return ['HHUS26_2', 'HHUS28_2', 'LUS24_2'];

    case 'LTHJA26':
    case 'THJA26':
      return ['LTHJA26', 'THJA26'];

    default:
      assertExhaustiveSwitch(currentHardware);
  }
}
